import { ColumnDef, sortingFns } from '@tanstack/react-table';
import React, { useMemo } from 'react';
import {
  ControllStatus,
  getRiskConsequencesName,
  getRiskEventsName,
  getRiskSourcesName,
  getScenarioStatusName,
} from 'src/api/types';
import { GTColumnSelectAnchor, GTExpander, GTTdm } from 'src/components/GenericTable';
import { formatDate, numeralFormat } from 'src/utils/misc';
import { filter } from 'lodash';
import { Link } from 'react-router-dom';
import { useScenario } from 'src/state/scenario';
import { GTInternalIds } from 'src/components/GenericTable/types';
import { IncompleteStateMarkerNew, renderNoData, TextOverflowTooltip } from 'src/common';
import { mpEvent } from 'src/utils/mixpanel/useMixPanel';
import { MPEvents } from 'src/utils/mixpanel/types';
import { encodeQueryParams } from 'src/api/client';
import useLocalStorage from 'src/hooks/useLocalStorage';
import { TableIds } from 'src/api/types/misc';
import { TLLink } from '../../../DashboardPage/comps';
import { AssessmentRiskRegisterQuery } from 'src/graphql/_generated';

type AssessmentScenario = NonNullable<AssessmentRiskRegisterQuery['assessment']>['scenarios'][number];

interface EntityIdentifier {
  id: string;
  name: string;
}

type UseColumns = (a: {
  data?: AssessmentScenario[];
  version?: string;
  setDeleteScenarioId: (a?: string) => void;
  showAllProjected?: boolean;
  assessmentId: string;
  onAddStrategy: (scenario: AssessmentScenario) => void;
  onAddControl: (scenario: AssessmentScenario) => void;
  onAddIncident: (scenario: AssessmentScenario) => void;
}) => {
  columns: ColumnDef<AssessmentScenario>[];
  GTColumnSelectAnchorExported: JSX.Element;
};

const defaultColumns = [
  'status_scen',
  'name',
  'source',
  'event',
  'consequence',
  'expected_loss_base',
  'expected_loss',
  'expected_loss_projected',
  'updated_at',
];

export const useColumns: UseColumns = ({
  data = [],
  version,
  setDeleteScenarioId,
  showAllProjected,
  assessmentId,
  onAddStrategy,
  onAddControl,
  onAddIncident,
}) => {
  const [selectedColumns, setSelectedColumns] = useLocalStorage(
    `${TableIds.assessmentScenarios}-columns`,
    defaultColumns,
  );
  const { openModal } = useScenario();

  const columns = useMemo(() => {
    const internalIds = [GTInternalIds.tdm, GTInternalIds.expander];

    const expectedLossColoumns: ColumnDef<AssessmentScenario>[] = showAllProjected
      ? [
          {
            id: 'expected_loss_base',
            header: 'Unmodified Expected Loss',
            accessorFn: (row) => row.baseRisk.expectedLoss,
            cell: (info) => {
              const value = info.getValue() as number;
              if (!value) return renderNoData();
              return numeralFormat(info.getValue() as number);
            },
          },
          {
            id: 'expected_loss',
            header: 'Live Expected Loss',
            accessorFn: (row) => row.liveRisk.expectedLoss,
            cell: (info) => {
              const value = info.getValue() as number;
              if (!value) return renderNoData();
              return numeralFormat(info.getValue() as number);
            },
          },
          {
            id: 'expected_loss_projected',
            header: 'Projected Expected Loss',
            accessorFn: (row) => row.projectedRisk.expectedLoss,
            cell: (info) => {
              const value = info.getValue() as number;
              if (!value) return renderNoData();
              return numeralFormat(info.getValue() as number);
            },
          },
        ]
      : [
          {
            id: 'expected_loss',
            header: 'Expected Loss',
            accessorFn: (row) => row.liveRisk.expectedLoss,
            cell: (info) => {
              const value = info.getValue() as number;
              if (!value) return renderNoData();
              return numeralFormat(info.getValue() as number);
            },
          },
        ];

    const list: ColumnDef<AssessmentScenario>[] = [
      {
        id: GTInternalIds.expander,
        header: () => null,
        cell: ({ row }) => {
          return row.getCanExpand() ? (
            <GTExpander onClick={row.getToggleExpandedHandler()} isExpanded={row.getIsExpanded()} />
          ) : null;
        },
        size: 1,
        maxSize: 20,
        enableSorting: false,
      },
      {
        id: 'ux_id',
        header: 'Id',
        accessorFn: (row) => row.id || null,
        cell: (info) => info.getValue(),
      },
      {
        id: 'status_scen',
        header: 'Status',
        accessorFn: (row) => getScenarioStatusName(row.status),
        cell: (info) => info.getValue(),
      },
      {
        id: 'name',
        header: 'Name',
        accessorFn: (row) => row.name,
        cell: (info) => (
          <TextOverflowTooltip>
            <Link
              className="table-link"
              to={`/risk-assessments/${assessmentId}/scenario/${info.row.original.id}${encodeQueryParams({ version })}`}
              onClick={(e) => {
                e.stopPropagation();
                mpEvent(MPEvents.Link, {
                  source: { value: ['Assessment'], params: { id: assessmentId } },
                  destination: { value: ['Scenario'], params: { id: info.row.original.id } },
                  tags: ['ASSESSMENT', 'SCENARIO'],
                });
              }}
            >
              {info.getValue() as string}
            </Link>
          </TextOverflowTooltip>
        ),

        sortUndefined: 'last',
        sortingFn: sortingFns.text,
      },
      {
        id: 'source',
        header: 'Source',
        accessorFn: (row) => getRiskSourcesName(row.source),
        cell: (info) => info.getValue(),
      },
      {
        id: 'event',
        header: 'Event',
        accessorFn: (row) => getRiskEventsName(row.event),
        cell: (info) => info.getValue(),
      },
      {
        id: 'consequence',
        header: 'Consequence',
        accessorFn: (row) => getRiskConsequencesName(row.consequence),
        cell: (info) => info.getValue(),
      },
      {
        id: 'management_strategy',
        header: 'Strategy',
        accessorFn: (row) => row.managementStrategy,
        cell: (info) => info.getValue(),
      },
      {
        id: 'risk_owner',
        header: 'Risk Owner',
        accessorFn: (row) => row.ownerName,
        cell: (info) => info.getValue(),
        sortingFn: sortingFns.text,
      },
      {
        id: 'likelihood_percent',
        header: 'Likelihood',
        accessorFn: (row) => {
          const times = row.liveRisk?.frequencyTimes || 0;
          const years = row.liveRisk?.frequencyYears || 1;
          if (years <= 0) return 0;
          const raw = (times / years) * 100;
          return Math.min(100, raw);
        },
        cell: (info) => `${Math.round(info.getValue() as number)}%`,
      },
      {
        id: 'consequence_lower',
        header: 'Consequence - Lower',
        accessorFn: (row) => row.liveRisk?.lower || null,
        cell: (info) => {
          const value = info.getValue() as number;
          return value ? numeralFormat(value) : renderNoData();
        },
      },
      {
        id: 'consequence_upper',
        header: 'Consequence - Upper',
        accessorFn: (row) => row.liveRisk?.upper || null,
        cell: (info) => {
          const value = info.getValue() as number;
          return value ? numeralFormat(value) : renderNoData();
        },
      },
      ...expectedLossColoumns,
      {
        id: '_control_count',
        header: 'Linked Controls',
        accessorFn: (row) => row.riskManagementPlans?.length || 0,
        cell: (info) => info.getValue(),
      },
      {
        id: '_control_count_live',
        header: 'Live Linked Controls',
        accessorFn: (row) =>
          row.riskManagementPlans?.filter((el) => el.control?.status === ControllStatus.Live).length || 0,
        cell: (info) => info.getValue(),
      },
      {
        id: '_modifiers',
        header: 'Risk Modifiers',
        accessorFn: (row) => {
          const mods = row.riskManagementPlans?.reduce((acc, el) => {
            if (el.modifiers) {
              acc += el.modifiers.length;
            }
            return acc;
          }, 0);

          return mods;
        },
        cell: (info) => info.getValue(),
      },
      {
        id: 'created_at',
        header: 'Created Date',
        accessorFn: (row) => row.meta?.createdAt || null,
        cell: ({ row }) => {
          const date = row.original.meta?.createdAt;
          return <TLLink date={date}>{formatDate(date, { formatType: `datetime` })}</TLLink>;
        },
      },
      {
        id: 'review_date',
        header: 'Review Date',
        accessorFn: (row) => row.reviewDate || null,
        cell: ({ row }) => {
          const date = row.original.reviewDate;
          return <TLLink date={date}>{formatDate(date, { formatType: `date` })}</TLLink>;
        },
      },
      {
        id: 'risk_identified_date',
        header: 'Identified Date',
        accessorFn: (row) => row.riskIdentifiedDate || '',
        cell: (info) => <TLLink date={info.getValue() as string}>{formatDate(info.getValue() as string)}</TLLink>,
      },
      {
        id: 'edited_at',
        header: 'Last Edited Date',
        accessorFn: (row) => row.editedAt || null,
        cell: ({ row }) => {
          const date = row.original.editedAt;
          return <TLLink date={date}>{formatDate(date, { formatType: 'datetime' })}</TLLink>;
        },
      },
      {
        id: 'updated_at',
        header: 'Last Updated Date',
        accessorFn: (row) => row.meta?.updatedAt || null,
        cell: ({ row }) => {
          const date = row.original.meta?.updatedAt;
          if (!date) return renderNoData();
          return <TLLink date={date}>{formatDate(date, { formatType: `datetime` })}</TLLink>;
        },
      },
      {
        id: GTInternalIds.tdm,
        header: () => null,
        cell: ({ row }) => {
          const onEditClick = !version ? () => openModal(row.original.id) : undefined;
          const onDeleteClick = !version ? () => setDeleteScenarioId(row.original.id) : undefined;
          const onAddStrategyClick = !version ? () => onAddStrategy(row.original) : undefined;
          const onAddControlClick = !version ? () => onAddControl(row.original) : undefined;
          const onAddIncidentClick = !version ? () => onAddIncident(row.original) : undefined;

          return (
            <div css="display: flex;grid-gap: 8px">
              <GTTdm
                onEditClick={onEditClick}
                onDeleteClick={onDeleteClick}
                onAddStrategyClick={onAddStrategyClick}
                onAddControlClick={onAddControlClick}
                onAddIncidentClick={onAddIncidentClick}
                extraComponentLeft={<IncompleteStateMarkerNew scenario={row.original} />}
              />
            </div>
          );
        },
        size: 1,
      },
    ];

    return filter(list, (el) => [...internalIds, ...selectedColumns].includes(el.id as string));
  }, [data, selectedColumns, version, showAllProjected]);

  const expectedLossList = showAllProjected
    ? [
        {
          key: 'expected_loss_base',
          title: 'Unmodified Expected Loss',
        },
        {
          key: 'expected_loss',
          title: 'Live Expected Loss',
        },
        {
          key: 'expected_loss_projected',
          title: 'Projected Expected Loss',
        },
      ]
    : [
        {
          key: 'expected_loss',
          title: 'Expected Loss',
        },
      ];

  const GTColumnSelectAnchorExported = (
    <GTColumnSelectAnchor
      tableId={TableIds.assessmentScenarios}
      options={[
        { key: 'ux_id', title: 'Id' },
        { key: 'status_scen', title: 'Status' },
        { key: 'name', title: 'Name' },
        { key: 'source', title: 'Source' },
        { key: 'event', title: 'Event' },
        { key: 'consequence', title: 'Consequence' },
        {
          key: 'management_strategy',
          title: 'Strategy',
        },
        {
          key: 'risk_owner',
          title: 'Risk Owner',
        },
        {
          key: 'likelihood_percent',
          title: 'Likelihood (%)',
        },
        {
          key: 'consequence_lower',
          title: 'Consequence - Lower',
        },
        {
          key: 'consequence_upper',
          title: 'Consequence - Upper',
        },
        ...expectedLossList,
        {
          key: '_control_count',
          title: 'Linked Controls',
        },
        {
          key: '_control_count_live',
          title: 'Live Linked Controls',
        },
        {
          key: '_modifiers',
          title: 'Risk Modifiers',
        },
        {
          key: 'created_at',
          title: 'Created Date',
        },
        {
          key: 'review_date',
          title: 'Review Date',
        },
        {
          key: 'risk_identified_date',
          title: 'Identified Date',
        },
        {
          key: 'edited_at',
          title: 'Last Edited Date',
        },
        {
          key: 'updated_at',
          title: 'Last Updated Date',
        },
      ]}
      defaultOptions={defaultColumns}
      value={selectedColumns}
      onChange={setSelectedColumns}
    />
  );

  return { columns, GTColumnSelectAnchorExported };
};
